'use client';
import { useIntl } from '@capsicum/common';
import {
	BaseCountrySelectInput,
	type BaseCountrySelectInputProps,
	BaseTextInput,
	type BaseTextInputProps
} from '@capsicum/common-web';
import { type EmailSMSLoginFormType, showEmailInputFormAtom } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { type FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from 'styled-components';

import type { Intersect } from '@capsicum/common';

const REGEX_AT_LEAST_3_DIGITS = /^\d{3,}$/;
const REGEX_ONLY_DIGITS = /^\d+$/;

export const EmailPhoneInput: FC<
	Intersect<BaseCountrySelectInputProps, BaseTextInputProps> & {
		className?: string;
		testID?: string;
	}
> = ({ className, testID, ...props }) => {
	// hooks
	const { $t } = useIntl();
	const { clearErrors, control, watch } = useFormContext<EmailSMSLoginFormType>();

	// state
	const [showEmailInput, setShowEmailInput] = useAtom(showEmailInputFormAtom);
	const id = watch('id');

	// useEffects
	useEffect(() => {
		if (showEmailInput && id && REGEX_AT_LEAST_3_DIGITS.test(id)) {
			setShowEmailInput(false);
		}
	}, [id, setShowEmailInput, showEmailInput]);

	useEffect(() => {
		if (!showEmailInput && id && !REGEX_ONLY_DIGITS.test(id)) {
			setShowEmailInput(true);
		}
	}, [id, setShowEmailInput, showEmailInput]);

	useEffect(() => {
		clearErrors();
	}, [showEmailInput, clearErrors]);

	return (
		<S.Wrapper className={className}>
			{!showEmailInput && (
				<Controller
					control={control}
					name="country"
					render={({ field, fieldState: { error } }) => (
						<S.CountrySelect
							{...props}
							{...field}
							error={
								error
									? {
											message: '',
											type: error.type
										}
									: undefined
							}
							labelProp="internationalCallingCode"
							testID={`${testID}-country-select`}
							variant="countryWithCodeAndFlag"
						/>
					)}
				/>
			)}

			<Controller
				control={control}
				name="id"
				render={({ field, fieldState: { error } }) => (
					<S.EmailPhoneNumber
						{...props}
						{...field}
						error={
							error
								? {
										message: '',
										type: error.type
									}
								: undefined
						}
						name="id"
						placeholder={$t('auth.emailPhoneNumberPlaceholder')}
						testID={`${testID}-id-input`}
						type="text"
					/>
				)}
			/>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		width: 100%;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const EmailPhoneNumber = styled(BaseTextInput)`
		flex-grow: 1;
	`;

	export const CountrySelect = styled(BaseCountrySelectInput)`
		width: 5em;
	`;
}
